// Settings › Campaigns' ad spend (BL-008): the words for each entry and for an ad platform's campaign
// with spend on none of the company's. The server spreads an entry over its days and links the
// platforms' campaigns; this only says it.

import { dateRangeText } from '@flowology/kernel';
import { money } from '../review/review-model.ts';
import { CHANNEL_WORDS, type Channel } from './campaigns-model.ts';

export type Platform = 'google_ads' | 'microsoft_ads' | 'lsa';
export type SpendSource = 'manual' | 'brook_crm' | 'google_ads' | 'microsoft_ads' | 'lsa';

export interface SpendEntry {
  id: string;
  campaign: { id: string; name: string } | null;
  channel: Channel;
  platformCampaign: { id: string | null; name: string | null } | null;
  from: string;
  to: string;
  amountCents: number;
  clicks: number | null;
  impressions: number | null;
  estimated: boolean;
  source: SpendSource;
  note: string | null;
  rowVersion: number;
}

export interface Unlinked {
  platform: Platform;
  platformCampaignId: string | null;
  platformCampaignName: string | null;
  amountCents: number;
  from: string;
  to: string;
}

const FROM: Record<SpendSource, string> = {
  manual: 'entered here',
  brook_crm: 'from AdTrack',
  google_ads: 'from Google Ads',
  microsoft_ads: 'from Microsoft Ads',
  lsa: 'from Local Services',
};

/** "Google – drains · Oct 1–31, 2026 · entered here", or the channel when it's on no campaign yet. */
export function spendMeans(e: SpendEntry): string {
  const on = e.campaign?.name ?? `${CHANNEL_WORDS[e.channel]}, not on a campaign`;
  const when = e.from === e.to ? dateRangeText(e.from, e.from) : dateRangeText(e.from, e.to);
  return [on, when, FROM[e.source], e.estimated ? 'estimated (spread from a lifetime total)' : null]
    .filter(Boolean)
    .join(' · ');
}

/** "Microsoft Ads: Drains – Victoria, $412.00 since Sep 10, 2026": a platform campaign to link. */
export function unlinkedMeans(u: Unlinked): string {
  const name = u.platformCampaignName ?? u.platformCampaignId ?? 'the whole account';
  return `${CHANNEL_WORDS[u.platform]}: ${name}, ${money(u.amountCents)} (${dateRangeText(u.from, u.to)})`;
}

/** Only what the office entered changes here; an import is corrected where it came from. */
export const editable = (e: SpendEntry) => e.source === 'manual';

/** Cents from what was typed ("1,234.50"), or null when it isn't an amount. */
export function centsTyped(s: string): number | null {
  const m = /^\s*\$?\s*(\d{1,3}(?:,\d{3})*|\d+)(?:\.(\d{1,2}))?\s*$/.exec(s);
  if (!m) return null;
  return Number(m[1]!.replace(/,/g, '')) * 100 + Number((m[2] ?? '0').padEnd(2, '0'));
}
